import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { networkManager } from '../services/NetworkManager';
import { DataBuild } from '../types';
import { useNotification } from '../contexts/NotificationContext';

interface BundleComparisonDetail {
  bundleName: string;
  gamedataA: any;
  gamedataB: any;
  status: 'match' | 'mismatch' | 'only-in-a' | 'only-in-b';
  differences?: string[];
}

const BundleComparisonPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showNotification } = useNotification();
  
  const [gamedataA, setGamedataA] = useState<DataBuild | null>(null);
  const [gamedataB, setGamedataB] = useState<DataBuild | null>(null);
  const [bundleName, setBundleName] = useState<string>('');
  const [comparison, setComparison] = useState<BundleComparisonDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const gamedataAId = searchParams.get('gamedataA');
    const gamedataBId = searchParams.get('gamedataB');
    const bundle = searchParams.get('bundle');
    
    if (gamedataAId && gamedataBId && bundle) {
      setBundleName(bundle);
      loadComparison(gamedataAId, gamedataBId, bundle);
    } else {
      showNotification('Missing comparison parameters', 'error', 5000);
      navigate('/data-comparison');
    }
  }, [searchParams, navigate, showNotification]);

  const loadComparison = async (gamedataAId: string, gamedataBId: string, bundleName: string) => {
    try {
      setLoading(true);
      
      // Get detailed information for both gamedata
      const [gamedataADetails, gamedataBDetails] = await Promise.all([
        networkManager.getDataBuild(gamedataAId),
        networkManager.getDataBuild(gamedataBId)
      ]);

      setGamedataA(gamedataADetails);
      setGamedataB(gamedataBDetails);

      // Extract bundles from both gamedata
      const bundlesA = gamedataADetails.bundles || [];
      const bundlesB = gamedataBDetails.bundles || [];

      // Find the specific bundle
      const bundleA = bundlesA.find((bundle: any) => bundle.name === bundleName);
      const bundleB = bundlesB.find((bundle: any) => bundle.name === bundleName);

      let status: 'match' | 'mismatch' | 'only-in-a' | 'only-in-b';
      let differences: string[] = [];

      if (bundleA && bundleB) {
        // Both have the bundle, check if they match
        const bundleAStr = JSON.stringify(bundleA, null, 2);
        const bundleBStr = JSON.stringify(bundleB, null, 2);
        
        if (bundleAStr === bundleBStr) {
          status = 'match';
        } else {
          status = 'mismatch';
          differences = findDifferences(bundleA, bundleB);
        }
      } else if (bundleA) {
        status = 'only-in-a';
      } else {
        status = 'only-in-b';
      }

      setComparison({
        bundleName,
        gamedataA: bundleA,
        gamedataB: bundleB,
        status,
        differences
      });

    } catch (err) {
      console.error('Error loading bundle comparison:', err);
      showNotification('Failed to load bundle comparison', 'error', 5000);
    } finally {
      setLoading(false);
    }
  };

  const findDifferences = (objA: any, objB: any, path: string = ''): string[] => {
    const differences: string[] = [];
    
    // Get all keys from both objects
    const allKeys = Array.from(new Set([...Object.keys(objA), ...Object.keys(objB)]));
    
    for (const key of allKeys) {
      const currentPath = path ? `${path}.${key}` : key;
      const valueA = objA[key];
      const valueB = objB[key];
      
      if (!(key in objA)) {
        differences.push(`Field "${currentPath}" only exists in Gamedata B`);
      } else if (!(key in objB)) {
        differences.push(`Field "${currentPath}" only exists in Gamedata A`);
      } else if (typeof valueA === 'object' && valueA !== null && typeof valueB === 'object' && valueB !== null) {
        // Recursively compare nested objects
        differences.push(...findDifferences(valueA, valueB, currentPath));
      } else if (valueA !== valueB) {
        differences.push(`Field "${currentPath}" differs: "${valueA}" vs "${valueB}"`);
      }
    }
    
    return differences;
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'match':
        return { color: 'text-green-600', bgColor: 'bg-green-100', text: 'Match' };
      case 'mismatch':
        return { color: 'text-red-600', bgColor: 'bg-red-100', text: 'Mismatch' };
      case 'only-in-a':
        return { color: 'text-blue-600', bgColor: 'bg-blue-100', text: 'Only in A' };
      case 'only-in-b':
        return { color: 'text-purple-600', bgColor: 'bg-purple-100', text: 'Only in B' };
      default:
        return { color: 'text-gray-600', bgColor: 'bg-gray-100', text: 'Unknown' };
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading bundle comparison...</div>
      </div>
    );
  }

  if (!comparison) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-red-600">Failed to load comparison</div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(comparison.status);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Bundle Comparison</h1>
          <p className="text-gray-600 mt-1">
            Comparing bundle "{bundleName}" between {gamedataA?.title} and {gamedataB?.title}
          </p>
        </div>
        <button
          onClick={() => navigate('/data-comparison')}
          className="btn btn-secondary"
        >
          <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Comparison
        </button>
      </div>

      {/* Status Card */}
      <div className="card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bgColor} ${statusInfo.color}`}>
              {statusInfo.text}
            </span>
            <h2 className="text-xl font-semibold text-gray-900">{bundleName}</h2>
          </div>
        </div>
        
        <div className="mt-4 text-sm text-gray-600">
          {comparison.status === 'match' && 'Bundles match perfectly'}
          {comparison.status === 'mismatch' && 'Bundles have different content'}
          {comparison.status === 'only-in-a' && `Bundle only exists in ${gamedataA?.title}`}
          {comparison.status === 'only-in-b' && `Bundle only exists in ${gamedataB?.title}`}
        </div>
      </div>

      {/* Differences Section */}
      {comparison.differences && comparison.differences.length > 0 && (
        <div className="card p-6">
          <div className="flex items-center mb-4">
            <div className="h-8 w-8 bg-red-100 rounded-lg flex items-center justify-center mr-3">
              <svg className="h-4 w-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Differences Found</h3>
          </div>
          
          <div className="space-y-2">
            {comparison.differences.map((difference, index) => (
              <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{difference}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bundle Content Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gamedata A Bundle */}
        <div className="card p-6">
          <div className="flex items-center mb-4">
            <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{gamedataA?.title}</h3>
          </div>
          
          {comparison.gamedataA ? (
            <div className="bg-gray-50 rounded-lg p-4">
              <pre className="text-xs text-gray-800 overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(comparison.gamedataA, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-gray-500">Bundle not found in this gamedata</p>
            </div>
          )}
        </div>

        {/* Gamedata B Bundle */}
        <div className="card p-6">
          <div className="flex items-center mb-4">
            <div className="h-8 w-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
              <svg className="h-4 w-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{gamedataB?.title}</h3>
          </div>
          
          {comparison.gamedataB ? (
            <div className="bg-gray-50 rounded-lg p-4">
              <pre className="text-xs text-gray-800 overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(comparison.gamedataB, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-gray-500">Bundle not found in this gamedata</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BundleComparisonPage;
